import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { JobConfigProvider } from './hooks/useJobConfig';
import Login from './pages/Login';
import RegisterInvite from './pages/RegisterInvite';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Schedule from './pages/Schedule';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import Customers from './pages/Customers';
import CustomerProfile from './pages/CustomerProfile';
import Technicians from './pages/Technicians';
import Equipment from './pages/Equipment';
import FieldEquipment from './pages/FieldEquipment';
import EquipmentDetail from './pages/EquipmentDetail';
import Vendors from './pages/Vendors';
import Users from './pages/Users';
import Reports from './pages/Reports';
import DailySummary from './pages/DailySummary';
import ShopTime from './pages/ShopTime';
import Settings from './pages/Settings';
import NotificationSettings from './pages/NotificationSettings';
import FormTemplates from './pages/FormTemplates';
import SafetyDocs from './pages/SafetyDocs';
import HelpContact from './pages/HelpContact';
import Billing from './pages/Billing';
import AdminDashboard from './pages/AdminDashboard';
import Landing from './pages/Landing';
import About from './pages/About';
import Home from './pages/Home';
import FieldCalendar from './pages/FieldCalendar';
import FieldWorkList from './pages/FieldWorkList';
import FieldWorkDetail from './pages/FieldWorkDetail';
import FieldTicketDetail from './pages/FieldTicketDetail';
import FieldTicketApprovals from './pages/FieldTicketApprovals';
import FieldWorkSettingsPage from './pages/FieldWorkSettingsPage';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import { Toaster } from './components/ui/sonner';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <JobConfigProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Landing />} />
          <Route path="/pricing" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register/:token" element={<RegisterInvite />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/home" element={<RoleProtectedRoute><Home /></RoleProtectedRoute>} />
          <Route path="/field" element={<Navigate to="/field/calendar" replace />} />
          <Route path="/field/calendar" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldCalendar /></RoleProtectedRoute>} />
          <Route path="/field/work" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldWorkList /></RoleProtectedRoute>} />
          <Route path="/field/work/settings" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldWorkSettingsPage /></RoleProtectedRoute>} />
          <Route path="/field/work/:eventId" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldWorkDetail /></RoleProtectedRoute>} />
          <Route path="/field/tickets/:ticketId" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldTicketDetail /></RoleProtectedRoute>} />
          <Route path="/field/approvals" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldTicketApprovals /></RoleProtectedRoute>} />
          <Route path="/field/equipment" element={<RoleProtectedRoute requiredPermission="field.calendar"><FieldEquipment /></RoleProtectedRoute>} />
          <Route path="/field/customers" element={<RoleProtectedRoute requiredPermission="customers"><Customers /></RoleProtectedRoute>} />
          <Route path="/field/customers/:id" element={<RoleProtectedRoute requiredPermission="customers"><CustomerProfile /></RoleProtectedRoute>} />
          <Route path="/field/vendors" element={<RoleProtectedRoute requiredPermission="vendors"><Vendors /></RoleProtectedRoute>} />
          <Route path="/field/forms" element={<RoleProtectedRoute requiredPermission="field.calendar"><FormTemplates /></RoleProtectedRoute>} />
          <Route path="/dashboard" element={<RoleProtectedRoute requiredPermission="dashboard"><Dashboard /></RoleProtectedRoute>} />
          <Route path="/schedule" element={<RoleProtectedRoute requiredPermission="schedule"><Schedule /></RoleProtectedRoute>} />
          <Route path="/jobs" element={<RoleProtectedRoute requiredPermission="jobs"><Jobs /></RoleProtectedRoute>} />
          <Route path="/jobs/:id" element={<RoleProtectedRoute requiredPermission="job-detail"><JobDetail /></RoleProtectedRoute>} />
          <Route path="/customers" element={<RoleProtectedRoute requiredPermission="customers"><Customers /></RoleProtectedRoute>} />
          <Route path="/customers/:id" element={<RoleProtectedRoute requiredPermission="customers"><CustomerProfile /></RoleProtectedRoute>} />
          <Route path="/technicians" element={<RoleProtectedRoute requiredPermission="technicians"><Technicians /></RoleProtectedRoute>} />
          <Route path="/equipment" element={<RoleProtectedRoute requiredPermission="equipment"><Equipment /></RoleProtectedRoute>} />
          <Route path="/equipment/:id" element={<RoleProtectedRoute requiredPermission="equipment"><EquipmentDetail /></RoleProtectedRoute>} />
          <Route path="/vendors" element={<RoleProtectedRoute requiredPermission="vendors"><Vendors /></RoleProtectedRoute>} />
          <Route path="/users" element={<RoleProtectedRoute requiredPermission="users"><Users /></RoleProtectedRoute>} />
          <Route path="/reports" element={<RoleProtectedRoute requiredPermission="reports"><Reports /></RoleProtectedRoute>} />
          <Route path="/daily-summary" element={<RoleProtectedRoute requiredPermission="daily-summary"><DailySummary /></RoleProtectedRoute>} />
          <Route path="/shop-time" element={<RoleProtectedRoute requiredPermission="shop-time"><ShopTime /></RoleProtectedRoute>} />
          <Route path="/settings" element={<RoleProtectedRoute requiredPermission="settings"><Settings /></RoleProtectedRoute>} />
          <Route path="/settings/notifications" element={<RoleProtectedRoute requiredPermission="settings"><NotificationSettings /></RoleProtectedRoute>} />
          <Route path="/form-templates" element={<RoleProtectedRoute requiredPermission="form-templates"><FormTemplates /></RoleProtectedRoute>} />
          <Route path="/help" element={<RoleProtectedRoute requiredPermission="help"><HelpContact /></RoleProtectedRoute>} />
          <Route path="/billing" element={<RoleProtectedRoute requiredPermission="billing"><Billing /></RoleProtectedRoute>} />
          {/* Admin & Compliance division — same records, own workspace */}
          <Route path="/admin/forms" element={<RoleProtectedRoute requiredPermission="form-templates"><FormTemplates /></RoleProtectedRoute>} />
          <Route path="/admin/compliance" element={<RoleProtectedRoute requiredPermission="reports.compliance"><Reports /></RoleProtectedRoute>} />
          <Route path="/admin/safety-docs" element={<RoleProtectedRoute requiredPermission="safety-docs"><SafetyDocs /></RoleProtectedRoute>} />
          <Route path="/admin/equipment" element={<RoleProtectedRoute requiredPermission="equipment"><Equipment /></RoleProtectedRoute>} />
          <Route path="/admin/equipment/:id" element={<RoleProtectedRoute requiredPermission="equipment"><EquipmentDetail /></RoleProtectedRoute>} />
          <Route path="/admin/vendors" element={<RoleProtectedRoute requiredPermission="vendors"><Vendors /></RoleProtectedRoute>} />
          <Route path="/admin/customers" element={<RoleProtectedRoute requiredPermission="customers"><Customers /></RoleProtectedRoute>} />
          <Route path="/admin/customers/:id" element={<RoleProtectedRoute requiredPermission="customers"><CustomerProfile /></RoleProtectedRoute>} />
          <Route path="/admin" element={<RoleProtectedRoute requiredPermission="admin"><AdminDashboard /></RoleProtectedRoute>} />
        </Routes>
        <Toaster position="top-right" duration={2000} />
      </BrowserRouter>
      </JobConfigProvider>
    </AuthProvider>
  );
}

export default App;